import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import { AutoFixHigh as AutoFixHighIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ConfirmDialog from '../common/ConfirmDialog';
import SalovareSelectField from '../common/SalovareSelectField';
import { fieldLabelSx } from '../common/formFieldStyles';
import { formatUtcDate } from '../../utils/dateFormatUtils';
import { duplicateGroupIncludesEmail } from '../../utils/freshaCustomerCsv';
import { BRAND, brandAlpha, BRAND_RADIUS } from '../../theme/brandTokens';

const formatPhone = (phone) => {
  if (!phone?.countryCode || !phone?.number) return null;
  return `${phone.countryCode} ${phone.number}`;
};

const formatGenre = (genre, t) => {
  if (genre === 1 || genre === '1') return t('dataImport.customers.genre.male');
  if (genre === 2 || genre === '2') return t('dataImport.customers.genre.female');
  return null;
};

const DetailRow = ({ label, value, emphasize = false }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '120px 1fr',
      gap: 1,
      alignItems: 'baseline',
      mb: 0.75,
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontWeight: emphasize ? 700 : 500,
        color: value ? 'text.primary' : 'text.disabled',
        wordBreak: 'break-word',
      }}
    >
      {value || '—'}
    </Typography>
  </Box>
);

const DuplicateRowCard = ({
  row,
  selected,
  selectable,
  onSelect,
  t,
}) => {
  const phone = formatPhone(row.phone);
  const genre = formatGenre(row.genre, t);
  const birthDate = row.birthDate ? formatUtcDate(row.birthDate) : null;

  return (
    <Box
      component={selectable ? 'button' : 'div'}
      type={selectable ? 'button' : undefined}
      onClick={selectable ? () => onSelect(row.id) : undefined}
      sx={{
        width: '100%',
        textAlign: 'left',
        border: selected ? `2px solid ${BRAND.accent}` : `1px solid ${BRAND.line}`,
        borderRadius: 2,
        p: 1.75,
        bgcolor: selected ? brandAlpha('accent', 0.06) : BRAND.background,
        cursor: selectable ? 'pointer' : 'default',
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        font: 'inherit',
        '&:hover': selectable
          ? {
            borderColor: BRAND.accent,
            bgcolor: brandAlpha('accent', 0.04),
          }
          : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: BRAND.primaryText }}>
          {t('dataImport.customers.mergeDialog.rowTitle', { row: row.rowNumber })}
        </Typography>
        {selectable && (
          <Radio
            checked={selected}
            value={row.id}
            size="small"
            tabIndex={-1}
            sx={{
              p: 0.25,
              color: BRAND.accentText,
              '&.Mui-checked': { color: BRAND.accentText },
            }}
          />
        )}
      </Box>

      <DetailRow label={t('dataImport.customers.fields.name')} value={row.name} emphasize />
      <DetailRow label={t('dataImport.customers.fields.email')} value={row.email} />
      <DetailRow label={t('dataImport.customers.fields.phone')} value={phone} />
      <DetailRow label={t('dataImport.customers.fields.genre')} value={genre} />
      <DetailRow label={t('dataImport.customers.fields.birthDate')} value={birthDate} />

      {row.freshaClientId ? (
        <DetailRow
          label={t('dataImport.customers.mergeDialog.freshaClientId')}
          value={row.freshaClientId}
        />
      ) : null}
    </Box>
  );
};

const isGroupResolved = (group, decisions) => {
  const current = decisions[group.id];
  if (!current?.action) return false;
  if (current.action === 'merge' && !current.primaryRowId) return false;
  return true;
};

const MergeDuplicateCustomersDialog = ({
  open,
  onClose,
  groups,
  decisions,
  onChangeDecision,
  onConfirm,
  onAutoResolve,
  importing = false,
}) => {
  const { t } = useTranslation();
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [autoResolveOpen, setAutoResolveOpen] = useState(false);

  useEffect(() => {
    if (!open || !groups?.length) {
      setActiveGroupId(null);
      setAutoResolveOpen(false);
      return;
    }
    setActiveGroupId((current) => (
      groups.some((group) => group.id === current) ? current : groups[0].id
    ));
  }, [open, groups]);

  const activeIndex = Math.max(0, groups.findIndex((group) => group.id === activeGroupId));
  const activeGroup = groups[activeIndex] || null;
  const decision = activeGroup ? decisions[activeGroup.id] : null;
  const selectingPrimary = decision?.action === 'merge';
  const includesEmail = duplicateGroupIncludesEmail(activeGroup);
  const isFirstGroup = activeIndex <= 0;
  const isLastGroup = activeIndex >= groups.length - 1;
  const currentResolved = activeGroup ? isGroupResolved(activeGroup, decisions) : false;
  const allResolved = groups.every((group) => isGroupResolved(group, decisions));

  const goToPrevious = () => {
    if (isFirstGroup) return;
    setActiveGroupId(groups[activeIndex - 1].id);
  };

  const goToNext = () => {
    if (isLastGroup || !currentResolved) return;
    setActiveGroupId(groups[activeIndex + 1].id);
  };

  return (
    <>
    <ScrollableDialog
      open={open}
      onClose={importing ? undefined : onClose}
      maxWidth="md"
      showCloseButton
      dividers
      title={t('dataImport.customers.mergeDialog.title')}
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button color="inherit" onClick={onClose} disabled={importing}>
            {t('common.cancel')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button color="inherit" onClick={goToPrevious} disabled={isFirstGroup || importing}>
              {t('common.back')}
            </Button>
            {isLastGroup ? (
              <PrimaryActionButton onClick={onConfirm} disabled={!allResolved || importing}>
                {t('dataImport.customers.mergeDialog.confirm')}
              </PrimaryActionButton>
            ) : (
              <PrimaryActionButton onClick={goToNext} disabled={!currentResolved || importing}>
                {t('common.next')}
              </PrimaryActionButton>
            )}
          </Box>
        </>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box
          component="button"
          type="button"
          onClick={() => !importing && setAutoResolveOpen(true)}
          disabled={importing}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            textAlign: 'left',
            border: 'none',
            borderRadius: BRAND_RADIUS.pill,
            px: 2,
            py: 1.5,
            cursor: importing ? 'not-allowed' : 'pointer',
            font: 'inherit',
            color: BRAND.onAccent,
            background: BRAND.accent,
            boxShadow: 'none',
            opacity: importing ? 0.65 : 1,
            transition: 'transform 0.15s ease, background-color 0.15s ease',
            '&:hover': importing ? undefined : {
              transform: 'translateY(-1px)',
              background: BRAND.accentDark,
            },
            '&:focus-visible': {
              outline: `2px solid ${BRAND.accent}`,
              outlineOffset: 2,
            },
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: brandAlpha('onAccent', 0.18),
              color: BRAND.onAccent,
            }}
          >
            <AutoFixHighIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {t('dataImport.customers.mergeDialog.autoResolve')}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.25, opacity: 0.9 }}>
              {t('dataImport.customers.mergeDialog.autoResolveHint')}
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t('dataImport.customers.mergeDialog.description')}
        </Typography>

        <Box>
          <Typography variant="subtitle2" sx={fieldLabelSx}>
            {t('dataImport.customers.mergeDialog.groupProgress', {
              current: activeIndex + 1,
              total: groups.length,
            })}
          </Typography>
          {activeGroup && (
            <Typography variant="body2" sx={{ fontWeight: 600, color: BRAND.accentText }}>
              {t(`dataImport.customers.mergeDialog.groupTypes.${activeGroup.type}`, {
                key: activeGroup.key,
                count: activeGroup.rows.length,
              })}
            </Typography>
          )}
        </Box>

        {groups.length > 1 && (
          <SalovareSelectField
            label={t('dataImport.customers.mergeDialog.selectGroup')}
            value={activeGroupId || ''}
            onChange={(event) => setActiveGroupId(event.target.value)}
          >
            {groups.map((group, index) => (
              <MenuItem key={group.id} value={group.id}>
                {t('dataImport.customers.mergeDialog.groupOption', {
                  current: index + 1,
                  total: groups.length,
                  label: t(`dataImport.customers.mergeDialog.groupTypes.${group.type}`, {
                    key: group.key,
                    count: group.rows.length,
                  }),
                })}
              </MenuItem>
            ))}
          </SalovareSelectField>
        )}

        {activeGroup && (
          <>
            <Box>
              <Typography variant="subtitle2" sx={fieldLabelSx}>
                {selectingPrimary
                  ? t('dataImport.customers.mergeDialog.primaryRow')
                  : t('dataImport.customers.mergeDialog.matches')}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
                {selectingPrimary
                  ? t('dataImport.customers.mergeDialog.primaryRowHelp')
                  : t('dataImport.customers.mergeDialog.matchesHelp')}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.5,
                }}
              >
                {activeGroup.rows.map((row) => (
                  <DuplicateRowCard
                    key={row.id}
                    row={row}
                    t={t}
                    selectable={selectingPrimary}
                    selected={selectingPrimary && decision.primaryRowId === row.id}
                    onSelect={(rowId) => onChangeDecision(activeGroup.id, {
                      ...decision,
                      primaryRowId: rowId,
                    })}
                  />
                ))}
              </Box>
            </Box>

            <RadioGroup
              value={decision?.action || ''}
              onChange={(event) => {
                const action = event.target.value;
                onChangeDecision(activeGroup.id, {
                  action,
                  memberIds: activeGroup.rows.map((row) => row.id),
                  primaryRowId: action === 'merge'
                    ? (decision?.primaryRowId || activeGroup.rows[0].id)
                    : null,
                });
              }}
            >
              <Typography variant="subtitle2" sx={{ ...fieldLabelSx, mb: 0.5 }}>
                {t('dataImport.customers.mergeDialog.accountQuestion')}
              </Typography>
              <FormControlLabel
                value="merge"
                control={<Radio />}
                label={t('dataImport.customers.mergeDialog.merge')}
              />
              <FormControlLabel
                value="keepSeparate"
                control={<Radio />}
                label={t('dataImport.customers.mergeDialog.keepSeparate')}
                disabled={includesEmail}
              />
            </RadioGroup>

            {includesEmail && (
              <Typography variant="caption" color="text.secondary">
                {t('dataImport.customers.mergeDialog.emailMustMerge')}
              </Typography>
            )}
          </>
        )}
      </Box>
    </ScrollableDialog>

    <ConfirmDialog
      open={autoResolveOpen}
      onClose={() => setAutoResolveOpen(false)}
      onConfirm={onAutoResolve}
      title={t('dataImport.customers.mergeDialog.autoResolveDialog.title')}
      confirmText={t('dataImport.customers.mergeDialog.autoResolveDialog.confirm')}
      confirmColor="primary"
      loading={importing}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="warning">
          {t('dataImport.customers.mergeDialog.autoResolveDialog.intro')}
        </Alert>

        <Typography variant="body2">
          {t('dataImport.customers.mergeDialog.autoResolveDialog.willHappen')}
        </Typography>

        <Box component="ul" sx={{ m: 0, pl: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography component="li" variant="body2">
            <strong>{t('dataImport.customers.mergeDialog.autoResolveDialog.sameEmailTitle')}</strong>
            {' '}
            {t('dataImport.customers.mergeDialog.autoResolveDialog.sameEmailBody')}
          </Typography>
          <Typography component="li" variant="body2">
            <strong>{t('dataImport.customers.mergeDialog.autoResolveDialog.sameNamePhoneTitle')}</strong>
            {' '}
            {t('dataImport.customers.mergeDialog.autoResolveDialog.sameNamePhoneBody')}
          </Typography>
          <Typography component="li" variant="body2">
            <strong>{t('dataImport.customers.mergeDialog.autoResolveDialog.sameNameOnlyTitle')}</strong>
            {' '}
            {t('dataImport.customers.mergeDialog.autoResolveDialog.sameNameOnlyBody')}
          </Typography>
          <Typography component="li" variant="body2">
            <strong>{t('dataImport.customers.mergeDialog.autoResolveDialog.samePhoneOnlyTitle')}</strong>
            {' '}
            {t('dataImport.customers.mergeDialog.autoResolveDialog.samePhoneOnlyBody')}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t('dataImport.customers.mergeDialog.autoResolveDialog.footer')}
        </Typography>
      </Box>
    </ConfirmDialog>
    </>
  );
};

export default MergeDuplicateCustomersDialog;
